<template>
  <div class="m-scene-container">
    <MScene ref="sceneRef" background="/static/image/air-monitor/scene.jpg" :polygons="[]" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import MScene from '@/components/mScene/index.vue';
import Marker from './components/marker.vue';
import PopupCard from './components/popup-card.vue';
import ctt from '@/utils/const/index';
import { genComponentInstance } from '@/utils/utils';
import { getOptions } from '@/utils/deviceUtils/state';
import { isEmpty } from '@/utils/is';
import { getExternalDeviceList, updateExternalDeviceStatus } from '@/api/air-monitor/device-center';
import { getMockData } from './data';

export default {
  components: { MScene },
  data() {
    return {
      outterDevices: [],
      markers: {},
      hasSwitchList: {
        isolation: e => {
          return !!e.isOpen;
        },
        respirator: e => {
          return !!e.isOpen;
        },
        ventilate: e => {
          return !!e.isOpen;
        }
      },
      hasBtnList: [ctt.CT1226, ctt.HT100, ctt.HT200, ctt.CR1224, ctt.CB12231, ctt.CB1223],
      iconDict: {
        [ctt.CT1226]: 'icon-leaf-2',
        [ctt.HT100]: 'icon-chemical-weapons',
        [ctt.HT200]: 'icon-chemical-weapons',
        [ctt.CR1224]: 'icon-nuclide',
        [ctt.CB12231]: 'icon-bio',
        [ctt.CB1223]: 'icon-bio',
        respirator: 'icon-custom',
        ventilate: 'icon-fan',
        isolation: 'icon-wind'
        // 'btn-off',
        // 'gas-mask',
        // 'nuclide',
        // 'leaf-2',
        // 'fan',
        // 'chemical-weapons',
        // 'bio'
      },
      infoPopup: {}
    };
  },
  computed: {
    ...mapState('socket', ['devicesSourseMap']),
    devices() {
      const res = this.devicesSourseMap.map(e => ({
        sn: e.sn,
        name: e.name,
        deviceType: e.deviceType,
        deviceTypeName: e.deviceTypeName,
        position: e.pointCoordinate?.split(' ').map(e => parseFloat(e)),
        state2: e.state2 || 0,
        state: e.state || 0,
        runstate: e.runstate,
        location: e.dynamicInfoMap?.location_desc
      }));

      return res.concat(this.outterDevices);
    }
  },
  methods: {
    async initOutterDevices() {
      const res = await getExternalDeviceList();

      if (res.code !== 200) return;

      const deviceList = res.data;

      this.outterDevices.push(
        ...deviceList.map(e => ({
          deviceFlag: 'outer',
          sn: e.id,
          name: e.name,
          deviceType: e.deviceTypeCode,
          deviceTypeName: e.deviceTypeName,
          position: e.coordinate?.split(' ').map(e => parseFloat(e)),
          state2: 0,
          state: 0,
          state3: e.switchState == '1' ? 'normal' : 'offline',
          isOpen: e.switchState == '1',
          runstate: Number(e.runstate),
          location: e.locationDesc
        }))
      );
    },
    upOutterDeviceState(e, isOpen, event) {
      event.stopPropagation();

      const text = isOpen ? this.$t('关闭') : this.$t('开启');

      this.$confirm(
        this.$t('确定{action}({name})吗?', { action: text, name: e.name }),
        this.$t('提示'),
        {
          confirmButtonText: this.$t('确定'),
          cancelButtonText: this.$t('取消'),
          type: 'warning'
        }
      ).then(() => {
        updateExternalDeviceStatus({ id: e.sn, switchState: isOpen ? 0 : 1 }).then(res => {
          if (res.code !== 200) return;
          this.$message({
            type: 'success',
            message: this.$t('{action}成功', { action: text }),
            showClose: true
          });

          const device = this.outterDevices.find(device => device.sn === e.sn);

          device.isOpen = !isOpen;
          device.state3 = !isOpen ? 'normal' : 'offline';
        });
      });
    },
    initDevices() {
      const scene = this.$refs.sceneRef.$scene;

      this.devices.forEach(e => {
        if (this.markers[e.sn]) this.markers[e.sn].remove();

        if (isEmpty(e.position)) return;

        const options = getOptions(e.runstate, e.state2);

        const animateDict = {
          respirator: ['scale'],
          ventilate: ['rotate'],
          isolation: ['scale']
        };

        const isOpen = this.hasSwitchList[e.deviceType]?.(e);

        this.markers[e.sn] = {
          record: null,
          instance: genComponentInstance(Marker, {
            deviceFlag: e.deviceFlag ? e.deviceFlag : '',
            state: options.code,
            state3: e.deviceFlag == 'outer' ? e.state3 : options.code,
            icon: this.iconDict[e.deviceType],
            hasSwitch: !!this.hasSwitchList[e.deviceType],
            isOpen,
            animate: isOpen ? animateDict[e.deviceType] : [],
            onClick: el => {
              this.showDeviceInfo(el, e);
            },
            onSwitch: (isOpen, event) => {
              this.upOutterDeviceState(e, isOpen, event);
            }
          }),
          remove() {
            this.instance?.dispose();
            this.record.remove();
          }
        };

        this.markers[e.sn].record = scene.addCssMarker(...e.position, {
          zIndex: e.position[1],
          createEl: () => {
            return this.markers[e.sn].instance.$mount().$el;
          }
        });
      });
    },
    showDeviceInfo(el, e) {
      Object.keys(this.infoPopup).forEach(sn => {
        if (this.infoPopup[sn]) {
          this.infoPopup[sn].instance.dispose();
          this.infoPopup[sn] = null;
        }
      });

      const options = getOptions(e.runstate, e.state2);

      const getBtn = e => {
        if (options.isOffline) return [];

        const list = [];

        if (this.hasBtnList.includes(e.deviceType)) {
          list.push({
            text: this.$t('详情'),
            onClick: () => {
              this.$router.push({
                name: 'Realdata',
                params: { sn: e.sn, deviceType: e.deviceType }
              });
            }
          });
        }

        if (options.isAlarm) {
          list.push(
            {
              text: this.$t('处置'),
              onClick: () => {
                this.$router.push({
                  name: 'AirMonitorAlarmInfo',
                  params: { sn: e.sn, deviceType: e.deviceType }
                });
              }
            },
            {
              text: this.$t('智能决策'),
              onClick: () => {
                this.initPolygonManager(e);
              }
            }
          );
        }

        return list;
      };

      this.infoPopup[e.sn] = {
        $el: null,
        instance: genComponentInstance(PopupCard, {
          state: options.code,
          info: {
            title: e.name,
            type: e.deviceTypeName,
            icon: this.iconDict[e.deviceType],
            location: e.location
          },
          btns: getBtn(e),
          onClose: () => {
            this.infoPopup[e.sn].instance.dispose();
            this.infoPopup[e.sn] = null;
          }
        })
      };

      this.infoPopup[e.sn].$el = this.infoPopup[e.sn].instance.$mount().$el;

      el.appendChild(this.infoPopup[e.sn].$el);
    },
    initPolygonManager(e) {
      const scene = this.$refs.sceneRef.$scene;

      const dict = {
        202506211604: 'left',
        '09060001800001': 'right'
      };

      const data = getMockData(dict[e.sn] || 'left');

      const polygonManage = scene.usePolygonSeedsFillAnimation({
        seeds: [],
        speed: 5,
        polygon: data.polygons.points
      });

      // polygonManage.drawPolygon();

      data.polygons.property.seeds.forEach(seed => {
        polygonManage.addSeedPoint(...seed);
      });

      scene.addTask('updatePolygonSeeds', () => {
        polygonManage.update();
      });

      polygonManage.onFinishing(() => {
        this.initPathManager(data.paths);
      });
    },
    initPathManager(paths) {
      const scene = this.$refs.sceneRef.$scene;

      const pathManager = scene.usePathManager();

      const color = '#00ff00';

      paths.forEach(path => {
        pathManager.addPath(path, color);
      });

      let i = 0;
      scene.addTask('updatePath', () => {
        i = i + 5;
        pathManager.draw(i);
        scene.isNeedUpdate = true;
      });
    }
  },
  mounted() {
    this.initOutterDevices();
    this.initDevices();
  },
  watch: {
    devices: {
      handler: 'initDevices',
      deep: true
    }
  }
};
</script>

<style lang="scss">
.m-scene-container {
  width: 100%;
  height: 100%;

  --scale: 1;
}
</style>
